import { KycAddress, KycData } from "@/types/kycReview";
import { STEP } from "../constants";
import { Draft, Errors, MarriageNameMatchesId, Uploads } from "../state/types";
import { getPath, hasValue, isYes } from "./paths";
import { occupationSlots } from "./occupation";
import {
  getManilaToday,
  requiresFreshIdImages,
  validateIdExpiration,
} from "./idValidation";
import { allRiskAnswered } from "./riskProfiler";
import { hasOnFileImage } from "./attachments";
import { isValidMobileNumber } from "./phone";

export interface ValidationContext {
  draft: Draft;
  originalKyc: KycData;
  uploads: Uploads;
  marriageNameMatchesId?: MarriageNameMatchesId;
  confirmed: boolean;
  isTier3: boolean;
  riskDirty: boolean;
  sameAsPresentAddress?: boolean; // permanent mirrors current; skip its validation
  today?: string; // injectable for deterministic tests
}

const FATCA_ROWS: Array<[string, string]> = [
  ["FATCA.usCitizen", "US citizen answer"],
  ["FATCA.usResident", "US resident answer"],
  ["FATCA.usAlien", "US resident alien answer"],
  ["FATCA.usPhone", "US telephone number answer"],
  ["FATCA.usBorn", "Born in the US answer"],
  ["FATCA.usAddressResidence", "US residence address answer"],
  ["FATCA.usAddressMailing", "US mailing address answer"],
  ["FATCA.usAccount", "US account standing instruction answer"],
  ["FATCA.usPOA", "US power of attorney answer"],
  ["FATCA.usCareOfAddress", "Care of or hold mail address answer"],
];

function req(
  errors: Errors,
  path: string,
  value: unknown,
  label: string
): void {
  if (!hasValue(value)) errors[path] = `${label} is required.`;
}

export function pepRequiresDocument(ctx: ValidationContext): boolean {
  return (
    ctx.isTier3 &&
    (isYes(getPath(ctx.draft.kyc, "PEP.governmentWork")) ||
      isYes(getPath(ctx.draft.kyc, "PEP.governmentRelative")))
  );
}

export function uploadedThisSession(uploads: Uploads, image: string): boolean {
  return uploads[image]?.status === "uploaded";
}

export function hasDocument(ctx: ValidationContext, image: string): boolean {
  return (
    uploadedThisSession(ctx.uploads, image) ||
    hasOnFileImage(ctx.originalKyc, image)
  );
}

function isMarried(kyc: KycData): boolean {
  return String(kyc.civil?.civilStatus || "").toLowerCase() === "married";
}

export function marriageCertificateRequired(ctx: ValidationContext): boolean {
  return isMarried(ctx.draft.kyc) && ctx.marriageNameMatchesId === "no";
}

function validateMarriageCertificate(
  errors: Errors,
  ctx: ValidationContext
): void {
  if (!isMarried(ctx.draft.kyc)) return;
  if (!hasValue(ctx.marriageNameMatchesId)) {
    errors.marriageNameMatchesId =
      "Please answer whether your married name matches your valid ID.";
    return;
  }
  if (marriageCertificateRequired(ctx) && !hasDocument(ctx, "marriageCert")) {
    errors.marriageCert = "Upload your marriage certificate.";
  }
}

// Fresh front+back uploads required (this session) per the backend ID rules.
export function idImagesRequired(ctx: ValidationContext): boolean {
  const ids = ctx.draft.kyc.ids || {};
  const originalIds = ctx.originalKyc.ids || {};
  return (
    requiresFreshIdImages({
      original: originalIds,
      draft: ids,
      hasExistingFront: hasOnFileImage(ctx.originalKyc, "idPhotoFront"),
      hasExistingBack: hasOnFileImage(ctx.originalKyc, "idPhotoBack"),
    })
  );
}

function validateAddress(
  errors: Errors,
  kyc: KycData,
  basePath: string,
  label: string
): void {
  const address = (getPath<KycAddress>(kyc, basePath) as KycAddress) || {};
  req(errors, `${basePath}.country`, address.country, `${label} country`);
  req(errors, `${basePath}.province`, address.province, `${label} province`);
  req(errors, `${basePath}.city`, address.city, `${label} city`);
  req(errors, `${basePath}.street`, address.street, `${label} street`);

  // Barangay and the 4-digit ZIP are Philippine-specific; other countries omit
  // the barangay concept and use varied postal formats.
  if (address.country === "Philippines") {
    req(errors, `${basePath}.barangay`, address.barangay, `${label} barangay`);
    req(errors, `${basePath}.zip`, address.zip, `${label} ZIP code`);
    if (hasValue(address.zip) && !/^\d{4}$/.test(String(address.zip))) {
      errors[`${basePath}.zip`] = `${label} ZIP code must be 4 digits.`;
    }
  }
}

function validateIds(errors: Errors, ctx: ValidationContext): void {
  const ids = ctx.draft.kyc.ids || {};
  const today = ctx.today || getManilaToday();
  const expirationError = validateIdExpiration(
    ids.idValidity,
    ids.hasExpiration !== false,
    today
  );
  if (expirationError) errors["ids.idValidity"] = expirationError;

  if (idImagesRequired(ctx)) {
    if (!uploadedThisSession(ctx.uploads, "idPhotoFront")) {
      errors.idPhotoFront = "Upload a current photo of the front of your ID.";
    }
    if (!uploadedThisSession(ctx.uploads, "idPhotoBack")) {
      errors.idPhotoBack = "Upload a current photo of the back of your ID.";
    }
  }
}

export function validateStep(step: number, ctx: ValidationContext): Errors {
  const errors: Errors = {};
  const { draft, isTier3 } = ctx;
  const kyc = draft.kyc;

  if (step === STEP.CLIENT) {
    req(errors, "user.mobileNumber", draft.user.mobileNumber, "Mobile number");
    if (
      hasValue(draft.user.mobileNumber) &&
      !isValidMobileNumber(draft.user.mobileNumber)
    ) {
      errors["user.mobileNumber"] = "Enter a valid mobile number.";
    }
    req(errors, "gender", kyc.gender, "Gender");
    req(errors, "civil.civilStatus", kyc.civil?.civilStatus, "Civil status");
    validateMarriageCertificate(errors, ctx);
    if (isTier3) {
      validateIds(errors, ctx);
    }
  }

  if (step === STEP.ADDRESS) {
    validateAddress(errors, kyc, "currentAddress", "Current address");
    if (isTier3) {
      // When permanent mirrors current it is submitted as the current address,
      // so it needs no separate validation.
      if (!ctx.sameAsPresentAddress) {
        validateAddress(errors, kyc, "permanentAddress", "Permanent address");
      }
      validateAddress(errors, kyc, "businessAddress", "Work/business address");
    }
  }

  if (step === STEP.PROFESSIONAL) {
    if (isTier3) {
      // Occupation + position are stored as a [occupation, position] array; both
      // halves are required. Keyed under the shared field path so editing either
      // input clears the error.
      const [occupation, position] = occupationSlots(
        kyc.work?.primaryOccupation
      );
      if (!hasValue(occupation) || !hasValue(position)) {
        errors["work.primaryOccupation"] =
          "Occupation and position are required.";
      }
      req(
        errors,
        "work.nameOfEmployer",
        kyc.work?.nameOfEmployer,
        "Name of company or employer"
      );
      req(
        errors,
        "work.businessNature",
        kyc.work?.businessNature,
        "Nature of business"
      );
      req(
        errors,
        "work.annualIncomeAmount",
        kyc.work?.annualIncomeAmount,
        "Annual income"
      );
    } else {
      req(
        errors,
        "work.primaryOccupation",
        kyc.work?.primaryOccupation,
        "Primary occupation"
      );
      req(
        errors,
        "work.sourceOfFunds",
        kyc.work?.sourceOfFunds,
        "Source of funds"
      );
      validateIds(errors, ctx);
    }
  }

  if (step === STEP.PEP && isTier3) {
    req(
      errors,
      "PEP.governmentWork",
      getPath(kyc, "PEP.governmentWork"),
      "Government work answer"
    );
    req(
      errors,
      "PEP.governmentRelative",
      getPath(kyc, "PEP.governmentRelative"),
      "Government relative answer"
    );
    if (pepRequiresDocument(ctx)) {
      req(errors, "PEP.banks", getPath(kyc, "PEP.banks"), "Banks");
      if (!hasDocument(ctx, "PEPDocument")) {
        errors.PEPDocument = "PEP supporting document is required.";
      }
    }
  }

  if (step === STEP.FATCA && isTier3) {
    req(
      errors,
      "FATCA.usPerson",
      getPath(kyc, "FATCA.usPerson"),
      "US Person answer"
    );
    if (isYes(getPath(kyc, "FATCA.usPerson"))) {
      FATCA_ROWS.forEach(([path, label]) =>
        req(errors, path, getPath(kyc, path), label)
      );
    }
  }

  if (step === STEP.RISK) {
    // QA: risk is only required when the user edits their answers. A no-change
    // record submits fine. When edited, all 9 must be answered (result then
    // auto-reveals — there is no "view result" gate).
    if (ctx.riskDirty && !allRiskAnswered(draft.riskQuestionnaire)) {
      errors.riskQuestionnaire = "Please answer all risk profiler questions.";
    }
  }

  if (step === STEP.SETTLEMENT && isTier3) {
    req(
      errors,
      "accountName",
      draft.accountDetails.accountName,
      "Account name"
    );
    req(errors, "bankName", draft.accountDetails.bankName, "Name of bank");
    req(
      errors,
      "bankAccountNumber",
      draft.accountDetails.bankAccountNumber,
      "Account number"
    );
    req(
      errors,
      "savingsAccount",
      draft.accountDetails.savingsAccount,
      "Account type"
    );
  }

  if (step === STEP.REVIEW) {
    if (!ctx.confirmed) errors.confirmed = "Please confirm before submitting.";
  }

  return errors;
}

export function firstErrorKey(errors: Errors): string | null {
  const keys = Object.keys(errors);
  return keys.length ? keys[0] : null;
}

// For submit: returns the first visible step with errors (REVIEW last), or null.
export function findFirstInvalidStep(
  ctx: ValidationContext,
  visible: number[]
): { step: number; errors: Errors } | null {
  for (const step of visible) {
    const errors = validateStep(step, ctx);
    if (Object.keys(errors).length > 0) return { step, errors };
  }
  return null;
}
